'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'HOME', href: '/' },
  { label: 'OPERATIONS', href: '/operations' },
  { label: 'VERIFY', href: '/verify' },
  { label: 'REGISTRY', href: '/threat-registry' },
  { label: 'FRAUD AWARENESS', href: '/fraud-awareness' },
  { label: 'BRIEFINGS', href: '/briefings' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const handleScroll = () => setMobileOpen(false);
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [mobileOpen]);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background/90 backdrop-blur-xl border-b border-border' :'bg-background/80 backdrop-blur-xl border-b border-border/40 lg:bg-transparent lg:backdrop-blur-none lg:border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 group shrink-0">
          <AppLogo size={32} className="shrink-0" />
          <div className="flex flex-col">
            <span className="text-foreground font-black tracking-[0.2em] text-sm uppercase leading-none group-hover:text-primary transition-colors duration-300">
              FORENSICDESK
            </span>
            <span className="bracket-tag text-muted-foreground leading-none mt-0.5">
              INTELLIGENCE OPS
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary navigation">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className="bracket-tag text-muted-foreground hover:text-foreground transition-colors duration-200 relative underline-crimson"
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* Primary CTA */}
        <div className="flex items-center gap-3">
          <Link
            href="/intake"
            className="shine-effect hidden sm:flex items-center gap-2 bg-primary hover:bg-red-700 text-primary-foreground px-4 py-2 md:px-6 md:py-2.5 bracket-tag transition-colors duration-200 border border-primary"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground threat-pulse shrink-0" />
            INITIATE TRIAGE
          </Link>

          {/* Mobile menu toggle */}
          <button
            className="lg:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            suppressHydrationWarning
          >
            <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
          </button>
        </div>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background/90 backdrop-blur-xl border-b border-border">
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1" aria-label="Mobile navigation">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="bracket-tag text-muted-foreground hover:text-foreground py-3 border-b border-border last:border-0 transition-colors duration-200"
              >
                {link?.label}
              </Link>
            ))}
            <Link
              href="/intake"
              onClick={() => setMobileOpen(false)}
              className="mt-3 flex items-center justify-center gap-2 bg-primary hover:bg-red-700 text-primary-foreground px-6 py-3 bracket-tag transition-colors duration-200"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground threat-pulse" />
              INITIATE TRIAGE
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}